import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { CheckCircle, Home, Accessibility, Heart, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { mockServices } from "../utils/mockData";
import BoutonRappel from "./BoutonRappel";

const Services = () => {
  const icons = {
    1: <Heart className="h-8 w-8 text-orange-700" />,
    2: <Home className="h-8 w-8 text-orange-700" />,
    3: <Accessibility className="h-8 w-8 text-orange-700" />
  };

  return (
    <section id="services" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Nos services MaPrimeAdapt' en Île-de-France
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            En tant qu'AMO agréé par l'ANAH, nous vous accompagnons dans tous vos projets d'adaptation de logement. 
            Notre expertise couvre l'ensemble de l'Île-de-France.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {mockServices.map((service) => (
            <Card key={service.id} className="border-2 hover:border-orange-200 transition-all duration-300 hover:shadow-lg group">
              <CardHeader className="text-center pb-6">
                <div className="mx-auto mb-4 w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                  {icons[service.id]}
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900 mb-2">
                  {service.title}
                </CardTitle>
                <CardDescription className="text-gray-700">
                  {service.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-6 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-orange-700 mb-4">
                      {service.price}
                    </p>
                    <Link to="/simulateur">
                      <Button className="w-full bg-orange-700 hover:bg-orange-800">
                        Tester mon éligibilité
                      </Button>
                    </Link>
                    {service.lien && (
                      <Link
                        to={service.lien}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-700 underline hover:text-orange-800"
                      >
                        {service.ancre}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Maillage interne : l'accueil, page la plus forte du site, ne pointait
            qu'au simulateur. Ces liens font remonter l'autorité vers les pages
            qui reçoivent des impressions mais restent en page 2 (requêtes réelles
            Search Console : « aide anah monte escalier », « douche adaptée »…). */}
        <nav aria-label="Nos domaines d'intervention" className="mb-16 -mt-6">
          <p className="text-center text-sm text-gray-600 mb-3">Nos domaines d'intervention :</p>
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <li><Link to="/salle-de-bain" className="text-orange-700 underline hover:text-orange-800">Adapter une douche ou une salle de bain</Link></li>
            <li><Link to="/monte-escalier" className="text-orange-700 underline hover:text-orange-800">Aide ANAH pour un monte-escalier</Link></li>
            <li><Link to="/accessibilite" className="text-orange-700 underline hover:text-orange-800">Accessibilité du logement</Link></li>
            <li><Link to="/exterieurs" className="text-orange-700 underline hover:text-orange-800">Aménagement des accès extérieurs</Link></li>
            <li><Link to="/simulateur" className="text-orange-700 underline hover:text-orange-800">Simuler mon aide MaPrimeAdapt'</Link></li>
          </ul>
        </nav>

        {/* Process Section */}
        <div className="bg-gray-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Notre accompagnement en 4 étapes
            </h3>
            <p className="text-gray-700 max-w-2xl mx-auto">
              De l'évaluation de votre éligibilité au paiement des aides, nous vous accompagnons à chaque étape
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Évaluation gratuite",
                description: "Test d'éligibilité et diagnostic autonomie liberté à domicile",
                duration: "72 heures"
              },
              {
                step: "02", 
                title: "Montage dossier",
                description: "Constitution du dossier MaPrimeAdapt' et demande d'aides",
                duration: "1 semaine"
              },
              {
                step: "03",
                title: "Réalisation travaux", 
                description: "Sélection artisans et suivi des travaux d'adaptation",
                duration: "2-4 mois"
              },
              {
                step: "04",
                title: "Paiement aides",
                description: "Versement des aides MaPrimeAdapt' sur votre compte",
                duration: "2-4 semaines"
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-orange-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {process.step}
                  </div>
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-orange-200 transform -translate-y-1/2"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-gray-700 text-sm mb-2">{process.description}</p>
                <p className="text-orange-700 text-xs font-medium">{process.duration}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <BoutonRappel
              libelle="Être rappelé gratuitement"
              variant="default"
              className="bg-orange-700 hover:bg-orange-800"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;